import { useEffect, useMemo, useState } from "react";
import { auth, db } from "../config/firebase";
import { usePlayerMatch } from "./usePlayerMatch";
import { useMatch } from "./useMatch";
import {
  compareRoundSolves,
  countWinsForRound,
  countLossesForRound,
  getFastestTimeForRound,
  getAverageTimeForRound,
  countRoundsWon,
  Match,
} from "../components/utils/MatchUtils";
import { formatNameToInitialLast } from "../components/utils/UserUtils";
import { useStream } from "./useStream";

export interface PlayerStats {
  name: string;
  wins: number;
  sets: number;
  losses: number;
  fastest: number | null;
  average: number | null;
}

export interface OverlayStats {
  match: Match | null;
  player: PlayerStats;
  opponent: PlayerStats;
  loading: boolean;
  error: Error | null;
}

export function useOverlayData(streamId: string): OverlayStats {
  const { matchId, loading: streamLoading, error: streamError } = useStream(streamId);
  const { match, loading: matchLoading, error: matchError } = useMatch(db, matchId || undefined);

  const currentPlayerId = useMemo(() => {
    if (match) {
      return match.uids[0];
    }
    return null
  }, [match, auth.currentUser]);

  const opponentId = useMemo(() => {
    if (match) {
      return match.uids[1];
    }
    return null
  }, [match, auth.currentUser]);

  const {
    player: playerP,
    roundSolves: roundSolvesP,
    loading: loadingP,
    error: errorP,
  } = usePlayerMatch(db, matchId || "", currentPlayerId);

  const {
    player: playerO,
    roundSolves: roundSolvesO,
    loading: loadingO,
    error: errorO,
  } = usePlayerMatch(db, matchId || "", opponentId);

  const [playerStats, setPlayerStats] = useState<PlayerStats>({
    name: "",
    wins: 0,
    sets: 0,
    losses: 0,
    fastest: null,
    average: null,
  });

  const [opponentStats, setOpponentStats] = useState<PlayerStats>({
    name: "",
    wins: 0,
    sets: 0,
    losses: 0,
    fastest: null,
    average: null,
  });

  useEffect(() => {
    if (
      match &&
      currentPlayerId &&
      opponentId &&
      !loadingP &&
      !loadingO &&
      !matchLoading
    ) {
      const round = match.currentRound;

      const roundsP = compareRoundSolves(roundSolvesP, roundSolvesO);
      const roundsO = compareRoundSolves(roundSolvesO, roundSolvesP);

      setPlayerStats({
        name: formatNameToInitialLast(playerP?.displayName + ""),
        wins: countWinsForRound(roundsP, round),
        sets: countRoundsWon(roundSolvesP, roundSolvesO, 3),
        losses: countLossesForRound(roundsP, round),
        fastest: getFastestTimeForRound(roundSolvesP, round),
        average: getAverageTimeForRound(roundSolvesP, round),
      });

      setOpponentStats({
        name: formatNameToInitialLast(playerO?.displayName + ""),
        wins: countWinsForRound(roundsO, round),
        sets: countRoundsWon(roundSolvesO, roundSolvesP, 3),
        losses: countLossesForRound(roundsO, round),
        fastest: getFastestTimeForRound(roundSolvesO, round),
        average: getAverageTimeForRound(roundSolvesO, round),
      });
    }
  }, [
    match,
    currentPlayerId,
    opponentId,
    roundSolvesP,
    roundSolvesO,
    loadingP,
    loadingO,
    matchLoading,
  ]);

  return {
    match: match,
    player: playerStats,
    opponent: opponentStats,
    loading: matchLoading || loadingP || loadingO,
    error: matchError || errorP || errorO || null,
  };
}
